import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaSearch,
  FaThLarge,
  FaList,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import "./Products.css";
import ProductCard from "./ProductCard";
import { catalogApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useCart } from "../context/CartContext";
import { buildCategoryTree } from "../utils/categories";
import { resolveAssetUrl } from "../utils/assets";
import { getDefaultCartExtras } from "../utils/variants";
import Seo from "../components/Seo";
import { getCategorySeo, PAGE_SEO } from "../constants/seo";
import { ProductCardSkeleton } from "../components/Skeleton";

function findCategoryBySlug(nodes, slug) {
  for (const node of nodes || []) {
    if (node.slug === slug) return node;
    const child = findCategoryBySlug(node.children, slug);
    if (child) return child;
  }
  return null;
}

const SORT_OPTIONS = [
  { value: "popularity", label: "Sort by popularity" },
  { value: "rating", label: "Sort by average rating" },
  { value: "newest", label: "Sort by latest" },
  { value: "price_asc", label: "Sort by price: low to high" },
  { value: "price_desc", label: "Sort by price: high to low" },
];

const SHOW_OPTIONS = [9, 18, 27];
const PRICE_RANGE = { min: 0, max: 2000, step: 10 };

function countDescendants(node) {
  const kids = node.children || [];
  return kids.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

function categoryMatchesQuery(node, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  if (node.name?.toLowerCase().includes(q)) return true;
  return (node.children || []).some((c) => categoryMatchesQuery(c, q));
}

function buildPageItems(current, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = new Set([1, totalPages, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => items.add(n));
  if (current >= totalPages - 2) {
    [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => items.add(n));
  }
  return [...items]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b)
    .reduce((acc, n, i, arr) => {
      if (i > 0 && n - arr[i - 1] > 1) acc.push("…");
      acc.push(n);
      return acc;
    }, []);
}

function categoryContainsSlug(node, slug) {
  if (!slug) return false;
  if (node.slug === slug) return true;
  return (node.children || []).some((c) => categoryContainsSlug(c, slug));
}

function CategoryTreeItem({ node, selected, onSelect, depth = 0, filterQuery = "" }) {
  const matchesSelf = !filterQuery || node.name?.toLowerCase().includes(filterQuery.toLowerCase());
  const visibleChildren = (node.children || []).filter((c) =>
    categoryMatchesQuery(c, filterQuery)
  );
  const hasChildren = visibleChildren.length > 0;
  const [open, setOpen] = useState(
    depth === 0 || categoryContainsSlug(node, selected) || Boolean(filterQuery)
  );

  useEffect(() => {
    if (categoryContainsSlug(node, selected) || filterQuery) setOpen(true);
  }, [node, selected, filterQuery]);

  if (filterQuery && !matchesSelf && !hasChildren) return null;

  const active = selected === node.slug;
  const childCount = countDescendants(node);

  return (
    <li className={`shop-cat-item depth-${depth} ${active ? "is-active" : ""}`}>
      <div className={`shop-cat-row ${active ? "active" : ""}`}>
        {hasChildren || (node.children || []).length > 0 ? (
          <button
            type="button"
            className="shop-cat-toggle"
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <FaChevronDown /> : <FaChevronRight />}
          </button>
        ) : (
          <span className="shop-cat-spacer" />
        )}
        <button
          type="button"
          className="shop-cat-link"
          onClick={() => onSelect(node.slug)}
        >
          <span className="shop-cat-name">{node.name}</span>
          {childCount > 0 && depth === 0 && (
            <span className="shop-cat-count">{childCount}</span>
          )}
        </button>
      </div>
      {hasChildren && open && (
        <ul className="shop-cat-children">
          {visibleChildren.map((child) => (
            <CategoryTreeItem
              key={child._id || child.id}
              node={child}
              selected={selected}
              onSelect={onSelect}
              depth={depth + 1}
              filterQuery={filterQuery}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cart, addToCart } = useCart();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 9, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [minPriceDraft, setMinPriceDraft] = useState(PRICE_RANGE.min);
  const [maxPriceDraft, setMaxPriceDraft] = useState(PRICE_RANGE.max);

  const selectedCategory = searchParams.get("category") || "";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "newest";
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 9);
  const minPrice = searchParams.get("minPrice") || "";
  const maxPrice = searchParams.get("maxPrice") || "";
  const stock = searchParams.get("stock") || "all";

  const categoryTree = useMemo(
    () => buildCategoryTree(categories),
    [categories]
  );

  const updateParams = (patch, { resetPage = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) next.delete(key);
      else next.set(key, String(value));
    });
    if (resetPage && !("page" in patch)) next.delete("page");
    setSearchParams(next);
  };

  useEffect(() => {
    catalogApi
      .getCategories()
      .then((res) => setCategories(res.data.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setSidebarSearch(search);
    setMinPriceDraft(
      minPrice === "" ? PRICE_RANGE.min : Math.min(Number(minPrice), PRICE_RANGE.max)
    );
    setMaxPriceDraft(
      maxPrice === "" ? PRICE_RANGE.max : Math.min(Number(maxPrice), PRICE_RANGE.max)
    );
  }, [search, minPrice, maxPrice]);

  const applyPriceRange = (minVal, maxVal) => {
    const min = Math.min(minVal, maxVal);
    const max = Math.max(minVal, maxVal);
    updateParams({
      minPrice: min <= PRICE_RANGE.min ? "" : min,
      maxPrice: max >= PRICE_RANGE.max ? "" : max,
    });
  };

  const filteredCategoryTree = useMemo(
    () => categoryTree.filter((node) => categoryMatchesQuery(node, categoryFilter)),
    [categoryTree, categoryFilter]
  );

  const pricePercent = {
    left: ((minPriceDraft - PRICE_RANGE.min) / (PRICE_RANGE.max - PRICE_RANGE.min)) * 100,
    right:
      100 -
      ((maxPriceDraft - PRICE_RANGE.min) / (PRICE_RANGE.max - PRICE_RANGE.min)) * 100,
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      page,
      limit: SHOW_OPTIONS.includes(limit) ? limit : 9,
      sort: SORT_OPTIONS.some((o) => o.value === sort) ? sort : "newest",
    };
    if (selectedCategory) params.category = selectedCategory;
    if (search) params.search = search;
    if (minPrice !== "") params.minPrice = minPrice;
    if (maxPrice !== "") params.maxPrice = maxPrice;
    if (stock === "in" || stock === "out") params.stock = stock;

    catalogApi
      .getProducts(params)
      .then((res) => {
        if (cancelled) return;
        setProducts(res.data.data.products || []);
        setMeta(res.data.meta || { page, limit, total: 0, pages: 1 });
      })
      .catch((error) => {
        if (!cancelled) {
          setProducts([]);
          setMeta({ page: 1, limit, total: 0, pages: 1 });
          toast.error(getErrorMessage(error, "Failed to load products"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, search, sort, page, limit, minPrice, maxPrice, stock]);

  const handleAddToCart = async (product) => {
    try {
      const extras = getDefaultCartExtras(product);
      await addToCart(product.id, 1, extras);
      toast.success("Added to cart");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add to cart"));
    }
  };

  const pageItems = buildPageItems(meta.page || page, meta.pages || 1);
  const showingFrom =
    meta.total === 0 ? 0 : ((meta.page || 1) - 1) * (meta.limit || limit) + 1;
  const showingTo = Math.min(
    (meta.page || 1) * (meta.limit || limit),
    meta.total || 0
  );

  const pageSeo = useMemo(() => {
    if (selectedCategory) {
      const category = findCategoryBySlug(categoryTree, selectedCategory);
      const categorySeo = getCategorySeo(selectedCategory, category?.name);
      if (categorySeo) {
        return {
          ...categorySeo,
          path: `/products?category=${selectedCategory}`,
        };
      }
    }

    if (search.trim()) {
      const query = search.trim();
      return {
        title: `Search: ${query} | SG Office Supplies`,
        description: `Search results for "${query}" — office and school supplies available in the UAE from SG Office Supplies.`,
        path: `/products?search=${encodeURIComponent(query)}`,
      };
    }

    return PAGE_SEO.shop;
  }, [selectedCategory, categoryTree, search]);

  const activeCategory = selectedCategory
    ? findCategoryBySlug(categoryTree, selectedCategory)
    : null;

  const pageTitle = useMemo(() => {
    if (search.trim() && activeCategory?.name) {
      return `Search in ${activeCategory.name}`;
    }
    if (search.trim()) return "Search results";
    if (activeCategory?.name) return activeCategory.name;
    return "Shop Office & School Supplies";
  }, [search, activeCategory]);

  return (
    <main className="shop-page">
      <Seo {...pageSeo} />
      <section className="shop-hero">
        <div className="shop-hero-inner">
          <h1>{pageTitle}</h1>
          <nav className="shop-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/products">Shop</Link>
            {activeCategory?.name && (
              <>
                <span>/</span>
                <span>{activeCategory.name}</span>
              </>
            )}
            {search.trim() && !activeCategory?.name && (
              <>
                <span>/</span>
                <span>Search</span>
              </>
            )}
          </nav>
        </div>
      </section>

      <section className="shop-section">
        <div className="shop-layout">
          <aside className="shop-sidebar">
            <div className="shop-widget">
              <h3>Product categories</h3>
              <div className="shop-cat-filter">
                <input
                  type="search"
                  placeholder="Filter categories..."
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter categories"
                />
              </div>
              <ul className="shop-cat-list">
                <li className={`shop-cat-item depth-0 ${!selectedCategory ? "is-active" : ""}`}>
                  <div
                    className={`shop-cat-row ${!selectedCategory ? "active" : ""}`}
                  >
                    <span className="shop-cat-spacer" />
                    <button
                      type="button"
                      className="shop-cat-link"
                      onClick={() => updateParams({ category: "" })}
                    >
                      <span className="shop-cat-name">All products</span>
                    </button>
                  </div>
                </li>
                {filteredCategoryTree.length === 0 ? (
                  <li className="shop-cat-empty">No matching categories</li>
                ) : (
                  filteredCategoryTree.map((node) => (
                    <CategoryTreeItem
                      key={node._id || node.id}
                      node={node}
                      selected={selectedCategory}
                      onSelect={(slug) => updateParams({ category: slug })}
                      filterQuery={categoryFilter}
                    />
                  ))
                )}
              </ul>
            </div>

            <div className="shop-widget">
              <h3>Search</h3>
              <form
                className="shop-search-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  updateParams({ search: sidebarSearch.trim() });
                }}
              >
                <input
                  type="search"
                  placeholder="Search for:"
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                />
                <button type="submit" aria-label="Search">
                  <FaSearch />
                </button>
              </form>
            </div>

            <div className="shop-widget">
              <h3>Price</h3>
              <div className="shop-price-range">
                <div className="shop-price-values">
                  <span>AED {Number(minPriceDraft).toFixed(0)}</span>
                  <span>AED {Number(maxPriceDraft).toFixed(0)}</span>
                </div>
                <div className="shop-price-slider">
                  <div
                    className="shop-price-track"
                    style={{
                      left: `${pricePercent.left}%`,
                      right: `${pricePercent.right}%`,
                    }}
                  />
                  <input
                    type="range"
                    min={PRICE_RANGE.min}
                    max={PRICE_RANGE.max}
                    step={PRICE_RANGE.step}
                    value={minPriceDraft}
                    aria-label="Minimum price"
                    onChange={(e) => {
                      const next = Math.min(Number(e.target.value), maxPriceDraft);
                      setMinPriceDraft(next);
                    }}
                    onMouseUp={(e) =>
                      applyPriceRange(Number(e.target.value), maxPriceDraft)
                    }
                    onTouchEnd={(e) =>
                      applyPriceRange(Number(e.target.value), maxPriceDraft)
                    }
                  />
                  <input
                    type="range"
                    min={PRICE_RANGE.min}
                    max={PRICE_RANGE.max}
                    step={PRICE_RANGE.step}
                    value={maxPriceDraft}
                    aria-label="Maximum price"
                    onChange={(e) => {
                      const next = Math.max(Number(e.target.value), minPriceDraft);
                      setMaxPriceDraft(next);
                    }}
                    onMouseUp={(e) =>
                      applyPriceRange(minPriceDraft, Number(e.target.value))
                    }
                    onTouchEnd={(e) =>
                      applyPriceRange(minPriceDraft, Number(e.target.value))
                    }
                  />
                </div>
                <div className="shop-price-bounds">
                  <span>AED {PRICE_RANGE.min}</span>
                  <span>AED {PRICE_RANGE.max}+</span>
                </div>
                {(minPrice !== "" || maxPrice !== "") && (
                  <button
                    type="button"
                    className="shop-filter-btn shop-filter-clear"
                    onClick={() => {
                      setMinPriceDraft(PRICE_RANGE.min);
                      setMaxPriceDraft(PRICE_RANGE.max);
                      updateParams({ minPrice: "", maxPrice: "" });
                    }}
                  >
                    Clear price filter
                  </button>
                )}
              </div>
            </div>

            <div className="shop-widget">
              <h3>Availability</h3>
              <div className="shop-stock-filter" role="group" aria-label="Stock availability">
                {[
                  { value: "all", label: "All products" },
                  { value: "in", label: "In stock" },
                  { value: "out", label: "Out of stock" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`shop-stock-option ${
                      stock === option.value ? "is-active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="stock-filter"
                      value={option.value}
                      checked={stock === option.value}
                      onChange={() =>
                        updateParams({
                          stock: option.value === "all" ? "" : option.value,
                        })
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="shop-widget shop-cart-widget">
              <h3>Cart</h3>
              {!cart.items?.length ? (
                <p className="shop-cart-empty">No products in the cart.</p>
              ) : (
                <>
                  <ul className="shop-cart-items">
                    {cart.items.slice(0, 5).map((item) => {
                      const img = resolveAssetUrl(item.image);
                      return (
                        <li key={item.id}>
                          {img ? (
                            <img src={img} alt="" />
                          ) : (
                            <span className="shop-cart-thumb" />
                          )}
                          <div>
                            <Link to={`/product/${item.slug || item.productId}`}>
                              {item.name}
                            </Link>
                            <span>
                              {item.quantity} × AED{" "}
                              {Number(item.price || 0).toFixed(2)}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="shop-cart-subtotal">
                    Subtotal: <strong>AED {Number(cart.subtotal || 0).toFixed(2)}</strong>
                  </p>
                  <Link to="/cart" className="shop-cart-link">
                    View cart
                  </Link>
                </>
              )}
            </div>
          </aside>
          <div className="shop-main">
            <div className="shop-toolbar">
              <div className="shop-toolbar-left">
                <label className="shop-sort">
                  <span className="visually-hidden">Sort by</span>
                  <select
                    value={sort}
                    onChange={(e) => updateParams({ sort: e.target.value })}
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="shop-view-toggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={viewMode === "grid" ? "active" : ""}
                    onClick={() => setViewMode("grid")}
                    aria-label="Grid view"
                  >
                    <FaThLarge />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "list" ? "active" : ""}
                    onClick={() => setViewMode("list")}
                    aria-label="List view"
                  >
                    <FaList />
                  </button>
                </div>
              </div>

              <div className="shop-toolbar-right">
                <p className="shop-result-count">
                  {loading
                    ? "Loading…"
                    : meta.total
                      ? `Showing ${showingFrom}–${showingTo} of ${meta.total} results`
                      : "No results"}
                </p>
                <label className="shop-show">
                  Show:
                  <select
                    value={SHOW_OPTIONS.includes(limit) ? limit : 9}
                    onChange={(e) =>
                      updateParams({ limit: e.target.value, page: 1 })
                    }
                  >
                    {SHOW_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {(search || selectedCategory || stock === "in" || stock === "out") && (
              <div className="shop-active-filters">
                {search && (
                  <span className="shop-filter-chip">
                    Search: “{search}”
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => updateParams({ search: "" })}
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedCategory && activeCategory && (
                  <span className="shop-filter-chip">
                    Category: {activeCategory.name}
                    <button
                      type="button"
                      aria-label="Clear category"
                      onClick={() => updateParams({ category: "" })}
                    >
                      ×
                    </button>
                  </span>
                )}
                {(stock === "in" || stock === "out") && (
                  <span className="shop-filter-chip">
                    {stock === "in" ? "In stock" : "Out of stock"}
                    <button
                      type="button"
                      aria-label="Clear stock filter"
                      onClick={() => updateParams({ stock: "" })}
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}

            {loading ? (
              <ProductCardSkeleton count={SHOW_OPTIONS.includes(limit) ? limit : 9} list={viewMode === "list"} />
            ) : products.length === 0 ? (
              <p className="shop-empty">No products found.</p>
            ) : (
              <div
                className={
                  viewMode === "list" ? "products-grid list-view" : "products-grid"
                }
              >
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    layout={viewMode}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}

            {(meta.pages || 0) > 1 && (
              <nav className="shop-pagination" aria-label="Product pagination">
                <ul>
                  {pageItems.map((item, idx) =>
                    item === "…" ? (
                      <li key={`e-${idx}`} className="ellipsis">
                        …
                      </li>
                    ) : (
                      <li key={item}>
                        <button
                          type="button"
                          className={item === (meta.page || page) ? "active" : ""}
                          onClick={() =>
                            updateParams({ page: item }, { resetPage: false })
                          }
                        >
                          {item}
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </nav>
            )}
          </div>

        </div>
      </section>
    </main>
  );
};

export default Products;
